import { useState } from 'react';
import { GitBranch, ExternalLink } from 'lucide-react';
import { githubApi } from '@/services/githubApi';
import { DEFAULT_GITHUB_APP_INSTALL_URL, startGitHubInstall } from './githubInstall';

const GITHUB_APP_INSTALL_URL = import.meta.env.REACT_APP_GITHUB_APP_URL
  || DEFAULT_GITHUB_APP_INSTALL_URL;

/**
 * Starts a GitHub App installation. The link is built only after the backend
 * issues a single-use `state` for this user and workspace, and GitHub hands
 * it back to the callback, which refuses a redirect without it. It navigates
 * in this tab: GitHub returns to the callback page when the install is done.
 */
export function InstallGitHubApp() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function start() {
    setBusy(true);
    setError(null);
    try {
      await startGitHubInstall({
        base: GITHUB_APP_INSTALL_URL,
        issueState: () => githubApi.issueInstallState(),
        navigate: (url) => window.location.assign(url),
      });
    } catch (err) {
      const data = (err as { response?: { data?: { message?: string; detail?: string } } })
        ?.response?.data;
      setError(data?.detail || data?.message || (err as Error)?.message
        || 'Could not start the GitHub connection.');
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col items-end gap-1">
      <button
        type="button"
        onClick={() => void start()}
        disabled={busy}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 text-sm font-medium hover:bg-slate-800 dark:hover:bg-slate-200 transition-colors disabled:opacity-60"
      >
        <GitBranch className="w-4 h-4" />
        Connect GitHub
        <ExternalLink className="w-3 h-3" />
      </button>
      {error && (
        <p role="alert" className="text-xs text-red-700 dark:text-red-400">{error}</p>
      )}
    </div>
  );
}
